<template>
  <div class="proposed-pay-plan-drawer">
    <div class="pppd__panel">
      <!-- Header -->
      <header class="pppd__header">
        <div class="pppd__header-bar">
          <div class="pppd__header-main">
            <div class="pppd__identity">
              <v-avatar size="32" class="pppd__avatar">
                <img
                  v-if="proposal.avatar_url"
                  :src="proposal.avatar_url"
                  :alt="proposal.employee"
                />
                <span v-else class="pppd__avatar-fallback">{{ employeeInitials }}</span>
              </v-avatar>
              <span class="pppd__employee-name">{{ proposal.employee }}</span>
              <span v-if="proposal.employee_title" class="pppd__employee-title">
                {{ proposal.employee_title }}
              </span>
            </div>
            <div class="pppd__header-meta">
              <VersionBadge :code="versionChipText" />
              <PayPlanLifecycleBadge status="Proposed" :approval-status="menuApprovalStatus" />
              <PayPlanPeriodStateBadge
                v-if="proposal.state"
                :state="proposal.state"
                filled
              />
              <span class="pppd__period-text">{{ headerPeriodText }}</span>
            </div>
          </div>
          <div class="pppd__header-actions">
            <ChatterIcon @click="$emit('open-chatter')" />
            <PayPlanRowMenu
              status="Proposed"
              :approval-status="menuApprovalStatus"
              @action="handleMenuAction"
            />
            <button
              type="button"
              class="pppd__icon-btn"
              aria-label="Close"
              @click="$emit('close')"
            >
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="pppd__period-bar-wrap">
          <PeriodBar
            :state="normalizedState"
            :label="periodLabel"
            :open-ended="isOpenEnded"
            :selectable="false"
          />
        </div>
      </header>

      <!-- Body -->
      <div class="pppd__body">
        <!-- Info banner -->
        <div class="pppd__info-banner" role="status">
          <div class="pppd__info-banner-icon" aria-hidden="true">
            <v-icon size="20" color="#ffffff">mdi-information-outline</v-icon>
          </div>
          <p class="pppd__info-banner-text">{{ infoBannerText }}</p>
        </div>

        <!-- Backend rejection (e.g. PAY_PLAN_TERMS_LOCKED) -->
        <div v-if="error" class="pppd__error" role="alert">
          <v-icon size="20" class="pppd__error-icon" aria-hidden="true">mdi-alert-circle</v-icon>
          <span class="pppd__error-text">{{ error }}</span>
        </div>

        <!-- Change summary (change plan only, changed fields) -->
        <div
          v-if="planType === 'change-existing-plan' && computedChanges.length"
          class="pppd__change-summary"
        >
          <p class="pppd__change-summary-title">
            Changes from current plan (V{{ currentPlan.version }})
          </p>
          <div
            v-for="change in computedChanges"
            :key="change.field"
            class="pppd__change-row"
          >
            <span class="pppd__change-label">{{ change.label }}</span>
            <span class="pppd__change-before">{{ change.beforeDisplay }}</span>
            <span class="pppd__change-arrow" aria-hidden="true">→</span>
            <span class="pppd__change-after">{{ change.afterDisplay }}</span>
            <span v-if="change.deltaDisplay" class="pppd__change-delta">{{
              change.deltaDisplay
            }}</span>
          </div>
        </div>

        <!-- Effective period -->
        <div class="pppd__effective-row">
          <EffectiveDateToEffectiveTo
            :effective-from="proposal.effective_from"
            :effective-to="proposal.effective_to"
            :locked="!isEditable"
            @update:effectiveFrom="handleProposalUpdate('effective_from', $event)"
            @update:effectiveTo="handleProposalUpdate('effective_to', $event)"
          />
        </div>

        <!-- Terms lock note (change plan only) -->
        <p v-if="planType === 'change-existing-plan'" class="pppd__terms-note">
          Terms belong to {{ termsPlanCode }}. To change them,
          <button type="button" class="pppd__terms-note-link" @click="$emit('propose-new-plan')">propose a new pay plan</button>.
        </p>

        <!-- Country & currency -->
        <div class="pppd__field-row">
          <div class="pppd__field-col pppd__field-col--picker">
            <CountryPicker
              picker-label="Country*"
              search-label="Search country"
              label="Type to search"
              :value="proposal.country"
              :isforce-readonly="!isEditable || termsLocked"
              @input="handleProposalUpdate('country', $event)"
            />
          </div>
          <div class="pppd__field-col pppd__field-col--picker">
            <CurrencyPicker
              picker-label="Currency*"
              search-label="Search Currency"
              label="Select Currency"
              :value="proposal.currency"
              :isforce-readonly="!isEditable || termsLocked"
              @input="handleProposalUpdate('currency', $event)"
            />
          </div>
        </div>

        <!-- Salary, FTE, pay basis -->
        <div class="pppd__field-row pppd__field-row--salary">
          <div class="pppd__field-col pppd__field-col--salary">
            <CurrencyAmountInput
              label="Base Salary*"
              :model-value="proposal.amount"
              :suffix="amountSuffix"
              :locked="!isEditable"
              @update:modelValue="handleProposalUpdate('amount', $event)"
            />
          </div>
          <div class="pppd__field-col">
            <label class="pppd__field-label">
              FTE
              <EXTooltip placement="top" title="From primary assignment">
                <v-icon small class="pppd__help-icon">mdi-help-circle-outline</v-icon>
              </EXTooltip>
            </label>
            <div v-if="isFteReadonly" class="field-readonly pppd__fte-readonly">
              <span>{{ fteDisplayLabel }}</span>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
            <v-text-field
              v-else
              :value="fteInputValue"
              type="number"
              min="0"
              max="1"
              step="0.05"
              dense
              outlined
              hide-details
              aria-label="FTE"
              class="pppd__fte-input"
              @input="handleFteInput"
            />
          </div>
          <div class="pppd__field-col pppd__field-col--basis">
            <ExpressedAs
              :model-value="expressedAsValue"
              label="Pay Basis"
              :locked="!isEditable || termsLocked"
              @update:modelValue="handlePayBasisUpdate"
            />
          </div>
        </div>

        <!-- Fair pay status -->
        <div v-if="readinessEnabled && fairPayStatusText" class="pppd__fair-pay-status">
          <span class="pppd__fairly-paid-badge">Fairly paid</span>
          <span class="pppd__fair-pay-status-text">{{ fairPayStatusText }}</span>
        </div>

        <!-- Rule A-40: Readiness off shows the range penetration instead of Fair Pay -->
        <PayRangePenetrationBar
          v-if="showPenetration"
          class="pppd__penetration"
          :range-penetration="rangePenetration"
          :currency-symbol="penetrationCurrencySymbol"
        />

        <!-- Fair Pay calculator -->
        <FairPayCalculatorAtEffectiveDateCard
          v-else-if="readinessEnabled"
          class="pppd__calculator"
          :calculator="calculatorMode"
          :as-of-date="formatFairPayDate(proposal.effective_from)"
          :position="calculatorPosition"
          :pay-range-version="calculatorPayRangeVersion"
          :pay-range-adjusted="calculatorPayRangeAdjusted"
          :fte-adjustment-applied="fteAdjusted"
          :current-readiness="readinessEnabled ? payRange1Fte.readiness : null"
          :fair-at-current="fairAtCurrent"
          :simulated-readiness="simulatedReadiness"
          :fair-at-simulated="fairAtSimulated"
          @update:simulatedReadiness="handleReadinessChange"
          @reset-simulation="resetSimulation"
        />

        <p v-if="fteAdjusted" class="pppd__adjusted-note">
          The proposed amount is actual pay at {{ proposal.fte }} FTE.
        </p>

        <!-- Approval status -->
        <div class="pppd__approval-status-row">
          <span class="pppd__approval-status-label">Approval status</span>
          <span class="pppd__approval-badge" :class="`pppd__approval-badge--${approvalStatus}`">
            {{ approvalStatusBadgeLabel }}
          </span>
          <span class="pppd__approval-status-text">{{ approvalStatusText }}</span>
        </div>

        <!-- Approvers -->
        <div class="pppd__approvers-card">
          <h3 class="pppd__approvers-title">Proposed Approvers</h3>
          <div v-if="approvers.length > 0" class="pppd__approvers-list">
            <div
              v-for="(approver, index) in approvers"
              :key="resolveApproverKey(approver)"
              class="pppd__approver-row"
            >
              <div class="pppd__approver-left">
                <span class="pppd__approver-avatar" aria-hidden="true">{{
                  approverInitials(approver.name)
                }}</span>
                <div class="pppd__approver-info">
                  <span class="pppd__approver-name">{{ approver.name }}</span>
                  <span v-if="approver.role" class="pppd__approver-role">{{ approver.role }}</span>
                </div>
                <span v-if="approver.default" class="pppd__approver-default">Default</span>
              </div>
              <div class="pppd__approver-right">
                <span
                  class="pppd__approver-status"
                  :class="`pppd__approver-status--${approver.status}`"
                >
                  {{ formatApproverStatus(approver.status) }}
                </span>
                <button
                  v-if="isEditable"
                  type="button"
                  class="pppd__approver-remove"
                  aria-label="Remove approver"
                  @click="$emit('remove-approver', index)"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </button>
              </div>
            </div>
            <button
              v-if="isEditable"
              type="button"
              class="pppd__add-approver"
              aria-label="Add Approver"
              @click="$emit('add-approver')"
            >
              <v-icon small left>mdi-plus</v-icon>
              Add Approver
            </button>
          </div>
          <div v-else class="pppd__zero-approvers">
            <p>
              No approvers assigned. Submitting self-approves this proposal; your name is recorded
              when it's executed.
            </p>
            <button
              v-if="isEditable"
              type="button"
              class="pppd__add-approver"
              aria-label="Add Approver"
              @click="$emit('add-approver')"
            >
              <v-icon small left>mdi-plus</v-icon>
              Add Approver
            </button>
          </div>
        </div>
      </div>

      <!-- Footer action bar -->
      <footer v-if="primaryAction.visible" class="pppd__footer">
        <StandardButton
          type="magenta"
          variant="primary"
          size="small"
          :disabled="primaryAction.disabled"
          @click="primaryAction.handler"
        >
          {{ primaryAction.label }}
        </StandardButton>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import CountryPicker from "@components/CountryPicker/CountryPicker.vue";
import CurrencyPicker from "@components/CurrencyPicker/CurrencyPicker.vue";
import CurrencyAmountInput from "@components/CurrencyAmountInput/CurrencyAmountInput.vue";
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import PayPlanRowMenu from "@components/PayPlanRowMenu/PayPlanRowMenu.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import ExpressedAs from "@components/ExpressedAs/ExpressedAs.vue";
import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";
import FairPayCalculatorAtEffectiveDateCard from "@components/FairPayCalculatorAtEffectiveDateCard/FairPayCalculatorAtEffectiveDateCard.vue";
import { formatAssignmentFte } from "@components/EmployeeToPrimaryAssignmentBar/employeeToPrimaryAssignmentBarUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import { getCurrencySymbol } from "@components/FairPayStatusShort/formatFairPayStatusAmount";

const props = defineProps({
  planType: {
    type: String,
    required: true,
    validator: (value) => ["new-plan", "change-existing-plan"].includes(value),
  },
  approvalStatus: {
    type: String,
    required: true,
    validator: (value) =>
      [
        "draft",
        "pending-approval",
        "approved",
        "self-approved",
        "pending-execution",
        "rejected",
      ].includes(value),
  },
  fteAdjusted: {
    type: Boolean,
    default: false,
  },
  readinessEnabled: {
    type: Boolean,
    default: true,
  },
  /** Rule A-40: { min, mid, max, position_pct } shown when Readiness is off. */
  rangePenetration: {
    type: Object,
    default: null,
  },
  proposal: {
    type: Object,
    required: true,
  },
  payRange1Fte: {
    type: Object,
    required: true,
  },
  payRangeAdjusted: {
    type: Object,
    default: null,
  },
  approvers: {
    type: Array,
    default: () => [],
  },
  currentPlan: {
    type: Object,
    default: null,
  },
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "submit",
  "execute",
  "commit",
  "reset-to-draft",
  "delete",
  "void",
  "add-approver",
  "remove-approver",
  "update:proposal",
  "open-chatter",
  "close",
  "propose-new-plan",
]);

const simulatedReadiness = ref(props.payRange1Fte.readiness ?? 75);

const isEditable = computed(() => props.approvalStatus === "draft");

const termsLocked = computed(() => {
  const flag = props.proposal.terms_locked;
  if (typeof flag === "boolean") return flag;
  return props.planType === "change-existing-plan";
});

const isFteReadonly = computed(() => !isEditable.value || termsLocked.value);

const fteInputValue = computed(() => String(props.proposal.fte ?? 1));

const termsPlanCode = computed(() => {
  if (props.currentPlan?.id != null && props.currentPlan.id !== "") {
    return `PP-${props.currentPlan.id}`;
  }
  return "the current plan";
});

const versionChipText = computed(() => {
  if (props.planType === "new-plan") {
    return "?";
  }
  return props.currentPlan ? `PP-${props.currentPlan.id}.?` : "?";
});

const menuApprovalStatus = computed(() => {
  const map = {
    draft: "Draft",
    "pending-approval": "Pending approval",
    approved: "Approved",
    "self-approved": "Approved",
    "pending-execution": "Approved",
    rejected: "Rejected",
  };
  return map[props.approvalStatus] || "Draft";
});

const primaryAction = computed(() => {
  const actions = {
    draft: {
      label: "Submit for approval",
      handler: () => emit("submit"),
      visible: true,
      disabled: false,
    },
    "pending-approval": {
      label: "",
      handler: () => {},
      visible: false,
      disabled: true,
    },
    approved: {
      label: "Execute",
      handler: () => emit("execute"),
      visible: true,
      disabled: false,
    },
    "self-approved": {
      label: "Execute",
      handler: () => emit("execute"),
      visible: true,
      disabled: false,
    },
    "pending-execution": {
      label: "Commit",
      handler: () => emit("commit"),
      visible: true,
      disabled: false,
    },
    rejected: {
      label: "Void",
      handler: () => emit("void"),
      visible: true,
      disabled: false,
    },
  };
  return (
    actions[props.approvalStatus] || {
      label: "",
      handler: () => {},
      visible: false,
      disabled: true,
    }
  );
});

const approvalStatusBadgeLabel = computed(() => {
  const labels = {
    draft: "Draft",
    "pending-approval": "Waiting",
    approved: "Approved",
    "self-approved": "Approved",
    "pending-execution": "Pending execution",
    rejected: "Rejected",
  };
  return labels[props.approvalStatus] || "";
});

const approvalStatusText = computed(() => {
  const approverCount = props.approvers.length;
  const approvedCount = props.approvers.filter((a) => a.status === "approved").length;

  if (props.approvalStatus === "draft") {
    return approverCount === 0
      ? "Not submitted · no approvers"
      : `Not submitted · ${approverCount} approver${approverCount > 1 ? "s" : ""} proposed`;
  }
  if (props.approvalStatus === "pending-approval") {
    const remaining = approverCount - approvedCount;
    if (approverCount === 1) {
      return "Waiting for 1 of 1 approval";
    }
    if (remaining <= 0) {
      return "All approvals collected";
    }
    return `${approvedCount} of ${approverCount} approvals collected, waiting for ${remaining}`;
  }
  if (props.approvalStatus === "approved" || props.approvalStatus === "self-approved") {
    return "All approvals collected";
  }
  if (props.approvalStatus === "pending-execution") {
    return "Executed · commit opens the Reconcile dialog (payroll check)";
  }
  if (props.approvalStatus === "rejected") {
    const rejecter = props.approvers.find((a) => a.status === "rejected");
    return rejecter
      ? `Rejected by ${rejecter.name} · void to free the slot`
      : "Rejected · void to free the slot";
  }
  return "";
});

/** Currency label: accepts a code string (Storybook) or a picker object. */
const currencyCode = computed(() => {
  const c = props.proposal.currency;
  if (!c) return "";
  return typeof c === "string" ? c : c.code || c.currency_code || "";
});

const periodLabel = computed(() => {
  const amount = formatAmountNumber(props.proposal.amount);
  const basis = formatPayBasisShort(props.proposal.pay_basis);
  return `${amount} ${currencyCode.value}/${basis}`;
});

const normalizedState = computed(() =>
  props.approvalStatus === "draft" ? "draft" : "proposed"
);

const isOpenEnded = computed(
  () => !props.proposal.effective_to || props.proposal.effective_to === null
);

const headerPeriodText = computed(() => {
  const from = formatFairPayDate(props.proposal.effective_from);
  const to = props.proposal.effective_to ? formatFairPayDate(props.proposal.effective_to) : "—";
  return `Effective from ${from} · Effective to ${to}`;
});

const infoBannerText = computed(() =>
  props.planType === "change-existing-plan"
    ? "This is a proposal to change the existing plan"
    : "This is a proposal for a new plan"
);

const amountSuffix = computed(() => {
  const basis = formatPayBasisShort(props.proposal.pay_basis);
  return `${currencyCode.value}/${basis}`;
});

const expressedAsValue = computed(() => mapPayBasisToExpressedAs(props.proposal.pay_basis));

const fteDisplayLabel = computed(() => {
  const fte = props.proposal.fte ?? 1;
  const n = Number(fte);
  if (Number.isFinite(n) && n === 1) {
    return "1 - FTE";
  }
  return `${formatAssignmentFte(fte)}`.replace(" FTE", " - FTE");
});

const employeeInitials = computed(() => {
  const parts = String(props.proposal.employee || "")
    .trim()
    .split(/\s+/);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
});

const fairPayStatusText = computed(() => {
  const pct = props.payRange1Fte.equivalentReadiness ?? props.payRange1Fte.readiness;
  if (pct == null) return "";
  return `Equivalent readiness ${pct}% · within ±2% tolerance`;
});

const showPenetration = computed(() => !props.readinessEnabled && props.rangePenetration != null);
const penetrationCurrencySymbol = computed(() =>
  getCurrencySymbol(currencyCode.value || "USD")
);

const calculatorMode = computed(() => {
  if (!props.readinessEnabled) {
    return "read-only";
  }
  return "current-and-simulated";
});

const calculatorPosition = computed(
  () =>
    props.payRange1Fte.position || {
      code: props.proposal.position_code || "#434-Electrician",
      name: props.proposal.position_name || "",
      fte: props.proposal.fte ?? 1,
    }
);

function buildAnchors(range) {
  if (range?.anchors) return range.anchors;
  const min = Number(range?.min) || 0;
  const max = Number(range?.max) || min;
  const span = max - min;
  return {
    0: min,
    50: min + span * 0.33,
    100: min + span * 0.67,
    150: max,
  };
}

const calculatorPayRangeVersion = computed(() => {
  if (props.payRange1Fte.payRangeVersion) {
    return props.payRange1Fte.payRangeVersion;
  }
  return {
    version: props.currentPlan?.version || 3,
    asOfDate: props.payRange1Fte.asOfDate || "Jan 1, 2024",
    code: props.payRange1Fte.code || "P-1456",
    jobCategory: props.payRange1Fte.jobCategory || "Electrician",
    level: props.payRange1Fte.level || "Level 1 · Beginner",
    currency: currencyCode.value || "USD",
    payBasis: expressedAsValue.value,
    location: props.payRange1Fte.location || "Metro Denver",
    countryFlag: props.payRange1Fte.countryFlag || "https://flagcdn.com/us.svg",
    anchors: buildAnchors(props.payRange1Fte),
  };
});

const calculatorPayRangeAdjusted = computed(() => {
  if (!props.fteAdjusted || !props.payRangeAdjusted) return null;
  if (props.payRangeAdjusted.payRangeVersion) {
    return props.payRangeAdjusted;
  }
  return {
    anchors: buildAnchors(props.payRangeAdjusted),
  };
});

const fairAtCurrent = computed(() => {
  if (!props.readinessEnabled) return null;
  if (props.payRange1Fte.fairAtCurrent) return props.payRange1Fte.fairAtCurrent;
  return {
    amount: Number(String(props.proposal.amount).replace(/,/g, "")) || 130000,
    currency: currencyCode.value || "USD",
    basis: "yr",
    variance: "±2%",
  };
});

const fairAtSimulated = computed(() => {
  if (!props.readinessEnabled) return null;
  if (props.payRange1Fte.fairAtSimulated) return props.payRange1Fte.fairAtSimulated;
  return fairAtCurrent.value;
});

const computedChanges = computed(() => {
  if (props.planType !== "change-existing-plan" || !props.currentPlan) return [];

  const changes = [];
  const fieldConfig = {
    amount: { label: "Base salary", format: formatAmountChange },
    effective_from: { label: "Effective from", format: formatFairPayDate },
    effective_to: { label: "Effective to", format: (v) => (v ? formatFairPayDate(v) : "—") },
  };

  Object.entries(fieldConfig).forEach(([field, config]) => {
    const before = props.currentPlan[field];
    const after = props.proposal[field];
    if (before === after) return;
    const entry = {
      field,
      label: config.label,
      beforeDisplay: config.format(before, props.currentPlan),
      afterDisplay: config.format(after, props.proposal),
      deltaDisplay: field === "amount" ? computeAmountDelta(before, after) : "",
    };
    changes.push(entry);
  });

  return changes;
});

function formatAmountNumber(value) {
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(value || "");
  return new Intl.NumberFormat("en-US").format(n);
}

function formatAmountChange(value, plan) {
  const amount = formatAmountNumber(value);
  const basis = formatPayBasisShort(plan?.pay_basis || props.proposal.pay_basis);
  const raw = plan?.currency || props.proposal.currency;
  const currency = typeof raw === "string" ? raw : raw?.code || raw?.currency_code || "";
  return `${amount} ${currency}/${basis}`;
}

function formatPayBasisShort(basis) {
  const map = {
    Year: "Year",
    Month: "Month",
    Hour: "Hour",
    per_year: "Year",
    per_month: "Month",
    per_hour: "Hour",
  };
  return map[basis] || "Year";
}

function mapPayBasisToExpressedAs(basis) {
  const map = {
    Year: "per_year",
    Month: "per_month",
    Hour: "per_hour",
    per_year: "per_year",
    per_month: "per_month",
    per_hour: "per_hour",
  };
  return map[basis] || "per_year";
}

function mapExpressedAsToPayBasis(value) {
  const map = {
    per_year: "Year",
    per_month: "Month",
    per_hour: "Hour",
  };
  return map[value] || "Year";
}

function computeAmountDelta(before, after) {
  const b = Number(String(before).replace(/,/g, ""));
  const a = Number(String(after).replace(/,/g, ""));
  if (!Number.isFinite(b) || !Number.isFinite(a) || b === 0) return "";
  const delta = a - b;
  const pct = ((delta / b) * 100).toFixed(1);
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${formatAmountNumber(delta)} (${sign}${pct}%)`;
}

function handleProposalUpdate(field, value) {
  if (!isEditable.value) return;
  emit("update:proposal", { ...props.proposal, [field]: value });
}

function handlePayBasisUpdate(value) {
  handleProposalUpdate("pay_basis", mapExpressedAsToPayBasis(value));
}

function handleFteInput(value) {
  if (!isEditable.value) return;
  const n = Number(value);
  emit("update:proposal", {
    ...props.proposal,
    fte: Number.isFinite(n) && value !== "" ? n : value,
  });
}

function handleReadinessChange(value) {
  simulatedReadiness.value = value;
}

function resetSimulation() {
  simulatedReadiness.value = props.payRange1Fte.readiness ?? 75;
}

function handleMenuAction(action) {
  switch (action) {
    case "submit":
      emit("submit");
      break;
    case "delete":
      emit("delete");
      break;
    case "reset_to_draft":
      emit("reset-to-draft");
      break;
    case "void":
      emit("void");
      break;
    default:
      break;
  }
}

function approverInitials(name) {
  return String(name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function resolveApproverKey(approver) {
  if (approver?.id != null && approver.id !== "") {
    return String(approver.id);
  }
  const name = String(approver?.name || "approver").trim();
  const role = String(approver?.role || "").trim();
  return role ? `${name}::${role}` : name;
}

function formatApproverStatus(status) {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
}
</script>

<style scoped>
.proposed-pay-plan-drawer {
  font-family: Inter, sans-serif;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 5px;
  max-width: 1536px;
  margin: 0 auto;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
}

.pppd__panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.pppd__header {
  flex-shrink: 0;
  padding: 0 24px;
  border-bottom: 1px solid #eaecf0;
}

.pppd__header-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
}

.pppd__header-main {
  flex: 1;
  min-width: 0;
}

.pppd__identity {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.pppd__avatar-fallback {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}

.pppd__employee-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
}

.pppd__employee-title {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #475467;
}

.pppd__header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
}

.pppd__period-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.pppd__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pppd__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.pppd__icon-btn:hover {
  background: #f9fafb;
}

.pppd__period-bar-wrap {
  width: 100%;
  padding: 11px 0 16px;
}

.pppd__period-bar-wrap :deep(.period-bar) {
  display: flex;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.pppd__period-bar-wrap :deep(.period-bar__label) {
  flex: 1 1 auto;
  text-align: center;
}

.pppd__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  gap: 17px;
  padding: 0 0 24px;
}

.pppd__footer {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #ffffff;
  border-top: 1px solid #eaecf0;
}

.pppd__info-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0 24px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.pppd__info-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #7f56d9;
  flex-shrink: 0;
}

.pppd__info-banner-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.pppd__error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 24px;
  padding: 10px 14px;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 8px;
}

.pppd__error-icon {
  color: #b42318;
  flex-shrink: 0;
}

.pppd__error-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #b42318;
}

.pppd__change-summary {
  margin: 0 24px;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #ffffff;
}

.pppd__change-summary-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.pppd__change-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #475467;
}

.pppd__change-label {
  width: 140px;
  flex-shrink: 0;
}

.pppd__change-after {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.pppd__change-delta {
  color: #475467;
}

.pppd__effective-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0 24px;
}

.pppd__terms-note {
  margin: 0 24px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.pppd__terms-note-link {
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 500;
  color: #7f56d9;
  text-decoration: underline;
  cursor: pointer;
}

.pppd__field-row {
  display: flex;
  flex-wrap: wrap;
  gap: 17px;
  padding: 0 24px;
}

.pppd__field-row--salary {
  align-items: flex-start;
}

.pppd__field-col {
  flex: 1 1 280px;
  min-width: 0;
}

.pppd__field-col--salary {
  flex: 1 1 312px;
}

/* ExSelectPicker uses v-container, which adds horizontal padding and shifts labels/inputs */
.pppd__field-col--picker :deep(.select-picker.container) {
  padding: 0;
  max-width: none;
}

.pppd__field-col--picker :deep(.label-name) {
  margin-bottom: 6px;
  color: #344054;
}

.pppd__field-col--basis :deep(.expressed-as) {
  max-width: none;
  width: 100%;
}

.pppd__field-col--basis :deep(.expressed-as__input) {
  height: 44px;
  box-sizing: border-box;
}

.pppd__field-col--salary :deep(.currency-amount-input) {
  max-width: none;
  width: 100%;
}

.pppd__field-col--salary :deep(.currency-amount-input__container) {
  width: 100%;
}

.pppd__field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.pppd__help-icon {
  color: #475467;
}

.field-readonly,
.pppd__fte-readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  height: 44px;
  box-sizing: border-box;
  background: #e0e0e0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #4d4d4d;
}

.pppd__lock-icon {
  color: #667085;
  flex-shrink: 0;
}

.pppd__fte-input {
  margin: 0;
}

.pppd__fte-input :deep(.v-input__slot) {
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
}

.pppd__fair-pay-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
}

.pppd__fairly-paid-badge {
  display: inline-flex;
  padding: 2px 8px;
  border: 1px solid #eaecf0;
  border-radius: 9999px;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.pppd__fair-pay-status-text {
  font-size: 12px;
  font-weight: 500;
  color: #475467;
}

.pppd__calculator,
.pppd__penetration {
  margin: 0 24px;
}

.pppd__adjusted-note {
  margin: 0 24px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.pppd__approval-status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
}

.pppd__approval-status-label {
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
}

.pppd__approval-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.pppd__approval-badge--draft {
  background: #f9fafb;
  border: 1px solid #eaecf0;
  color: #344054;
}

.pppd__approval-badge--pending-approval {
  background: #fffaeb;
  border: 1px solid #fedf89;
  color: #b54708;
}

.pppd__approval-badge--approved,
.pppd__approval-badge--self-approved {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  color: #067647;
}

.pppd__approval-badge--pending-execution {
  background: #eff8ff;
  border: 1px solid #b2ddff;
  color: #175cd3;
}

.pppd__approval-badge--rejected {
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
}

.pppd__approval-status-text {
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
}

.pppd__approvers-card {
  margin: 0 24px;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #ffffff;
}

.pppd__approvers-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.pppd__approver-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #eaecf0;
}

.pppd__approver-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.pppd__approver-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #f9f5ff;
  font-size: 11px;
  font-weight: 600;
  color: #6941c6;
  flex-shrink: 0;
}

.pppd__approver-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pppd__approver-name {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.pppd__approver-role {
  font-size: 12px;
  color: #475467;
}

.pppd__approver-default {
  padding: 2px 6px;
  border: 1px solid #abefc6;
  border-radius: 4px;
  background: #ecfdf3;
  font-size: 11px;
  font-weight: 500;
  color: #344054;
}

.pppd__approver-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pppd__approver-status {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.pppd__approver-status--pending {
  background: #fffaeb;
  border: 1px solid #fedf89;
  color: #344054;
}

.pppd__approver-status--approved {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  color: #344054;
}

.pppd__approver-status--rejected {
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #344054;
}

.pppd__approver-remove {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: #667085;
}

.pppd__add-approver {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #7f56d9;
}

.pppd__zero-approvers {
  font-size: 13px;
  line-height: 1.5;
  color: #475467;
}

.pppd__zero-approvers p {
  margin: 0 0 12px;
}
</style>
